import { get, post, del } from './httpClient';

/**
 * Identidades del usuario (0..N). Cuelgan del JWT, no de una empresa.
 * Al compartir, el dueño elige con cuál se pinta la página. Contrato:
 * `argenlotapi/docs/frontend/FRONTEND_BRANDS.md`.
 *
 * `/getuserbranding` sigue respondiendo, pero ya no pinta al visitante:
 * la lectura sale de `brands`.
 *
 * @see docs/maps/brands.md
 */

/** Los que acepta `/getbrandlogopresignedurl`. */
export const BRAND_LOGO_CONTENT_TYPES = ['image/png', 'image/jpeg', 'image/webp'];

/** El PUT sube la imagen entera a S3, sin pasar por la Lambda. */
const LOGO_UPLOAD_TIMEOUT_MS = 60000;

function isAbortError(error) {
  return error?.name === 'AbortError';
}

function readApiError(response, fallback) {
  const text = response?.error || response?.message;
  const trimmed = typeof text === 'string' ? text.trim() : '';
  return trimmed || fallback;
}

function withStatus(payload, response) {
  return {
    ...payload,
    _httpStatus: response?._httpStatus ?? null,
  };
}

function readText(value) {
  return typeof value === 'string' ? value : '';
}

/**
 * Fila de `brands` con las claves del contrato y sin nulls: el form las usa
 * como value de inputs controlados.
 *
 * @param {object|null|undefined} raw
 * @returns {object|null}
 */
export function normalizeBrand(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const id = String(raw.id ?? '').trim();
  if (!id) return null;
  return {
    id,
    user_id: readText(raw.user_id),
    brand_name: readText(raw.brand_name),
    logo_url: readText(raw.logo_url),
    primary_color: readText(raw.primary_color),
    secondary_color: readText(raw.secondary_color),
    phone: readText(raw.phone),
    whatsapp: readText(raw.whatsapp),
    email: readText(raw.email),
    is_default: raw.is_default === true,
    can_manage: raw.can_manage === true,
  };
}

/**
 * GET /getmybrands. JWT. Identidades de la cuenta, principal primero.
 * `can_manage` y `can_create_brand` son siempre true (son las tuyas).
 * `company_id` / `company_name` pueden venir null: no se usan.
 *
 * @param {{ signal?: AbortSignal }} [options]
 * @returns {Promise<{
 *   ok: boolean,
 *   source: 'api'|'none',
 *   brands: object[],
 *   canCreateBrand: boolean,
 *   message?: string,
 *   _httpStatus: number|null,
 * }>}
 */
export async function getMyBrands({ signal } = {}) {
  try {
    const response = await get('/getmybrands', { signal });
    if (response._httpOk) {
      const payload = response.data && typeof response.data === 'object' ? response.data : {};
      const list = Array.isArray(payload.brands) ? payload.brands : [];
      return withStatus({
        ok: true,
        source: 'api',
        brands: list.map(normalizeBrand).filter(Boolean),
        // El contrato lo manda siempre true; si el GET no lo trae, igual se puede crear.
        canCreateBrand: payload.can_create_brand !== false,
      }, response);
    }
    return withStatus({
      ok: false,
      source: 'api',
      brands: [],
      canCreateBrand: false,
      message: readApiError(response, 'No se pudieron cargar tus identidades.'),
    }, response);
  } catch (error) {
    if (isAbortError(error)) throw error;
    console.error('Marcas: GET /getmybrands falló', error);
    return {
      ok: false,
      source: 'none',
      brands: [],
      canCreateBrand: false,
      message: 'No se pudo hablar con el servidor.',
      _httpStatus: null,
    };
  }
}

/**
 * POST /upsertbrand. JWT, dueño de la identidad. Sin `brand_id` crea (201),
 * con `brand_id` actualiza (200) y acepta parciales: mandá solo lo que cambió.
 * String vacío **limpia** el campo; para no tocarlo, omitilo.
 * `company_id` se ignora: no lo mandes.
 *
 * El servidor normaliza (hex de seis dígitos en mayúsculas, email en
 * minúsculas), así que la respuesta puede diferir de lo enviado sin que haya
 * habido un cambio real. Una clave desconocida rechaza el request entero.
 *
 * @param {{
 *   brand_id?: string,
 *   brand_name?: string,
 *   logo_url?: string,
 *   primary_color?: string,
 *   secondary_color?: string,
 *   phone?: string,
 *   whatsapp?: string,
 *   email?: string,
 *   is_default?: boolean,
 * }} payload
 * @param {{ signal?: AbortSignal }} [options]
 * @returns {Promise<{
 *   ok: boolean,
 *   source: 'api'|'none',
 *   brand: object|null,
 *   message?: string,
 *   _httpStatus: number|null,
 * }>}
 */
export async function upsertBrand(payload = {}, { signal } = {}) {
  try {
    const response = await post('/upsertbrand', { body: payload, signal });
    if (response._httpOk) {
      const raw = response.data && typeof response.data === 'object' ? response.data : response;
      return withStatus({ ok: true, source: 'api', brand: normalizeBrand(raw) }, response);
    }
    const fallback = response._httpStatus === 404
      ? 'No se encontró esa identidad.'
      : 'No se pudo guardar la identidad.';
    return withStatus({
      ok: false,
      source: 'api',
      brand: null,
      message: readApiError(response, fallback),
    }, response);
  } catch (error) {
    if (isAbortError(error)) throw error;
    console.error('Marcas: POST /upsertbrand falló', error);
    return {
      ok: false,
      source: 'none',
      brand: null,
      message: 'No se pudo hablar con el servidor.',
      _httpStatus: null,
    };
  }
}

/**
 * DELETE /deletebrand?brand_id=. JWT, dueño. 400 si es la principal y quedan
 * otras (hay que marcar otra antes); si es la única, se borra.
 * Los shares que la usaban caen a la principal, no se rompen.
 *
 * @param {string} brandId
 * @param {{ signal?: AbortSignal }} [options]
 * @returns {Promise<{
 *   ok: boolean,
 *   source: 'api'|'none',
 *   message?: string,
 *   _httpStatus: number|null,
 * }>}
 */
export async function deleteBrand(brandId, { signal } = {}) {
  const id = String(brandId || '').trim();
  if (!id) {
    return {
      ok: false,
      source: 'none',
      message: 'Falta la identidad a eliminar.',
      _httpStatus: null,
    };
  }
  try {
    const response = await del('/deletebrand', { params: { brand_id: id }, signal });
    if (response._httpOk) {
      return withStatus({ ok: true, source: 'api' }, response);
    }
    return withStatus({
      ok: false,
      source: 'api',
      message: readApiError(response, 'No se pudo eliminar la identidad.'),
    }, response);
  } catch (error) {
    if (isAbortError(error)) throw error;
    console.error('Marcas: DELETE /deletebrand falló', error);
    return {
      ok: false,
      source: 'none',
      message: 'No se pudo hablar con el servidor.',
      _httpStatus: null,
    };
  }
}

/**
 * GET /getbrandlogopresignedurl. La marca tiene que existir antes: la key de
 * S3 incluye su id.
 *
 * @param {{ brandId: string, contentType: string, filename?: string }} params
 * @param {{ signal?: AbortSignal }} [options]
 * @returns {Promise<{
 *   ok: boolean,
 *   source: 'api'|'none',
 *   upload: object|null,
 *   message?: string,
 *   _httpStatus: number|null,
 * }>}
 */
export async function getBrandLogoPresignedUrl({ brandId, contentType, filename } = {}, { signal } = {}) {
  const id = String(brandId || '').trim();
  if (!id) {
    return {
      ok: false,
      source: 'none',
      upload: null,
      message: 'Guardá la identidad antes de subir el logo.',
      _httpStatus: null,
    };
  }
  try {
    const response = await get('/getbrandlogopresignedurl', {
      params: {
        brand_id: id,
        contentType,
        ...(filename ? { filename } : {}),
      },
      signal,
    });
    if (response._httpOk && response.data) {
      return withStatus({ ok: true, source: 'api', upload: response.data }, response);
    }
    return withStatus({
      ok: false,
      source: 'api',
      upload: null,
      message: readApiError(response, 'No se pudo preparar la subida del logo.'),
    }, response);
  } catch (error) {
    if (isAbortError(error)) throw error;
    console.error('Marcas: GET /getbrandlogopresignedurl falló', error);
    return {
      ok: false,
      source: 'none',
      upload: null,
      message: 'No se pudo hablar con el servidor.',
      _httpStatus: null,
    };
  }
}

/**
 * PUT de la imagen a la URL prefirmada. El `Content-Type` es exactamente el
 * que se pidió en el paso anterior: si no coincide, S3 responde
 * `SignatureDoesNotMatch`.
 *
 * `fetch` directo a propósito: httpClient serializa el body a JSON y apunta a
 * `API_BASE_URL`, y acá va un binario a S3 sin `Authorization`.
 *
 * @param {{ presignedUrl: string, file: File|Blob, contentType: string }} params
 * @returns {Promise<{ ok: boolean, message?: string, _httpStatus: number|null }>}
 */
async function putLogoToS3({ presignedUrl, file, contentType } = {}) {
  const controller = new AbortController();
  let timedOut = false;
  const timeoutId = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, LOGO_UPLOAD_TIMEOUT_MS);

  try {
    const response = await fetch(presignedUrl, {
      method: 'PUT',
      headers: { 'Content-Type': contentType },
      body: file,
      signal: controller.signal,
    });
    if (response.ok) return { ok: true, _httpStatus: response.status };
    return {
      ok: false,
      message: 'No se pudo subir el logo. Probá de nuevo.',
      _httpStatus: response.status,
    };
  } catch (error) {
    if (timedOut) {
      return {
        ok: false,
        message: 'La subida tardó demasiado. Probá con una conexión más estable.',
        _httpStatus: null,
      };
    }
    console.error('Marcas: PUT del logo a S3 falló', error);
    return { ok: false, message: 'No se pudo subir el logo.', _httpStatus: null };
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Sube el logo de una marca existente y lo persiste: presigned → PUT → upsert.
 *
 * @param {{ brandId: string, file: File }} params
 * @returns {Promise<{
 *   ok: boolean,
 *   brand: object|null,
 *   logoUrl: string,
 *   message?: string,
 * }>}
 */
export async function uploadBrandLogo({ brandId, file } = {}) {
  if (!file) {
    return { ok: false, brand: null, logoUrl: '', message: 'Elegí una imagen.' };
  }
  if (!BRAND_LOGO_CONTENT_TYPES.includes(file.type)) {
    return { ok: false, brand: null, logoUrl: '', message: 'Formato no permitido. Usá PNG, JPEG o WebP.' };
  }

  const presigned = await getBrandLogoPresignedUrl({
    brandId,
    contentType: file.type,
    filename: file.name,
  });
  if (!presigned.ok || !presigned.upload?.presignedUrl) {
    return { ok: false, brand: null, logoUrl: '', message: presigned.message || 'No se obtuvo la URL de subida.' };
  }

  const { presignedUrl, s3Url } = presigned.upload;
  const uploaded = await putLogoToS3({ presignedUrl, file, contentType: file.type });
  if (!uploaded.ok) {
    return { ok: false, brand: null, logoUrl: '', message: uploaded.message };
  }

  const logoUrl = String(s3Url || '').trim();
  if (!logoUrl) {
    return { ok: false, brand: null, logoUrl: '', message: 'No se recibió la URL del logo.' };
  }

  const saved = await upsertBrand({ brand_id: brandId, logo_url: logoUrl });
  if (!saved.ok) {
    return { ok: false, brand: null, logoUrl: '', message: saved.message };
  }

  return { ok: true, brand: saved.brand, logoUrl: saved.brand?.logo_url || logoUrl };
}
